/**
 * PDP Subscription Options
 *
 * Keeps the selected selling plan on the product form. The picker itself lives OUTSIDE the form
 * (it is a `_product-details` block, the form is inside `blocks/buy-buttons.liquid`), so the
 * chosen plan is mirrored into a hidden `selling_plan` input that lives inside the form —
 * `assets/product-form.js` submits with `new FormData(form)`, so anything in the form is posted.
 *
 * Card state mirrors live: the group whose radio is checked gets `is-active` (white panel, near
 * black border, 500-weight title); the checked frequency pill is styled by CSS `:has()`.
 */

const HIDDEN_INPUT_NAME = 'selling_plan';

class SubscriptionOptions extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#handleChange);

    // The form can be re-rendered (variant change morphs the buy-buttons block), which drops the
    // hidden input. Re-assert it on the way into every add-to-cart rather than trusting one write.
    document.addEventListener('click', this.#handleDocumentClick, true);

    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    document.removeEventListener('click', this.#handleDocumentClick, true);
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#sync();
  };

  #handleDocumentClick = (event) => {
    const target = /** @type {Element|null} */ (event.target);

    if (!target?.closest?.('.add-to-cart-button, [type="submit"]')) return;

    this.#sync();
  };

  /**
   * Reflect the checked group + frequency into card state and the form's hidden input.
   */
  #sync() {
    const groups = this.querySelectorAll('[data-plan-group]');
    let selectedPlanId = '';

    for (const group of groups) {
      const groupRadio = group.querySelector('.pdp-subscription__radio');
      const isActive = groupRadio instanceof HTMLInputElement ? groupRadio.checked : false;

      group.classList.toggle('is-active', isActive);

      if (!isActive) continue;

      const checkedPlan = group.querySelector('.pdp-subscription__frequency-input:checked');

      if (checkedPlan instanceof HTMLInputElement) {
        selectedPlanId = checkedPlan.value;
      }
    }

    this.#writeHiddenInput(selectedPlanId);
  }

  /**
   * @param {string} planId - selling plan id, or '' to clear the input.
   */
  #writeHiddenInput(planId) {
    const form = this.#findForm();

    if (!form) return;

    let input = form.querySelector(`input[name="${HIDDEN_INPUT_NAME}"]`);

    if (!planId) {
      input?.remove();
      return;
    }

    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = HIDDEN_INPUT_NAME;
      form.appendChild(input);
    }

    input.value = planId;
  }

  /**
   * The add-to-cart form for THIS product — scoped to the surrounding product section so a second
   * product form on the page (quick add, featured product) can't be picked up by mistake.
   * @returns {HTMLFormElement|null}
   */
  #findForm() {
    const scope = this.closest('.shopify-section, .product-information') || document;

    return /** @type {HTMLFormElement|null} */ (
      scope.querySelector('form[data-type="add-to-cart-form"]') ||
        document.querySelector('form[data-type="add-to-cart-form"]')
    );
  }
}

if (!customElements.get('subscription-options')) {
  customElements.define('subscription-options', SubscriptionOptions);
}
